/**
 * ReportRunner — runs one of the 10 preconfigured (canned) reports.
 *
 * Route: /admin/reports/:reportKey
 * All reports share:
 *   - site filter (uses listSitesLite)
 *   - date range (today / this_week / this_month / last_30d / last_90d / custom)
 *   - CSV export
 *
 * Group-by + aggregation differs per report. Implemented client-side over
 * the booking rows returned by `fetchBookingsForReport`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Download, RefreshCw } from "lucide-react";
import { listSitesLite, getAvailabilityInputs, listBlackoutsForGrid } from "@/lib/api";
import { bayRules, openMinutes, DEAD_STATUSES } from "@/lib/availability";
import { useViewAsClient } from "@/lib/viewAsClient";
import { CANNED_REPORTS } from "@/pages/Reports";
import { fetchBookingsForReport, resolveRange, downloadCsv } from "@/lib/reportsApi";
import { statusBadgeClass } from "@/lib/constants";
import { toast } from "sonner";
import { AuDateInput } from "@/components/AuDateTime";

const RANGES = [
  { value: "today",       label: "Today" },
  { value: "this_week",   label: "This week" },
  { value: "this_month",  label: "This month" },
  { value: "last_30d",    label: "Last 30 days" },
  { value: "last_90d",    label: "Last 90 days" },
  { value: "custom",      label: "Custom range" },
];

export default function ReportRunner() {
  const { reportKey } = useParams();
  const meta = CANNED_REPORTS.find((r) => r.key === reportKey);
  const { viewAsClientId } = useViewAsClient();

  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [range, setRange] = useState("this_month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listSitesLite().then(({ data }) => {
      if (!cancelled) setSites(data || []);
    });
    return () => { cancelled = true; };
  }, []);

  const run = async () => {
    setLoading(true);
    const { from: f, to: t } = resolveRange(range, from, to);
    const { data, error } = await fetchBookingsForReport({
      clientId: viewAsClientId || null,
      siteId: siteId || null,
      from: f, to: t,
    });
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  };

  // Auto-run once on mount + when filters change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { from: f, to: t } = resolveRange(range, from, to);
      const { data, error } = await fetchBookingsForReport({
        clientId: viewAsClientId || null,
        siteId: siteId || null,
        from: f, to: t,
      });
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reportKey, viewAsClientId, siteId, range, from, to]);

  // Open-hour capacity per bay (Bay utilisation %): site ∩ bay hours, open days, breaks, blackouts.
  const [capacity, setCapacity] = useState(null);
  useEffect(() => {
    if (reportKey !== "bay-utilisation") return undefined;
    let cancelled = false;
    setCapacity(null);
    (async () => {
      const { from: f, to: t } = resolveRange(range, from, to);
      const fromMs = new Date(f).getTime();
      const toMs = Math.min(new Date(t).getTime(), fromMs + 366 * 86400000);
      const ids = siteId ? [siteId] : ((await listSitesLite(viewAsClientId || null)).data || []).map((x) => x.id);
      const cap = new Map();
      for (const id of ids) {
        const [inp, bo] = await Promise.all([getAvailabilityInputs(id), listBlackoutsForGrid(id, f, t)]);
        for (const bay of inp.bays) {
          const blackouts = (bo.data || []).filter((x) => !x.bay_id || x.bay_id === bay.id);
          cap.set(bay.id, {
            bay: bay.name, site: inp.site?.name || "—",
            openHours: openMinutes(fromMs, toMs, bayRules(inp.site, bay), blackouts) / 60,
          });
        }
      }
      if (!cancelled) setCapacity(cap);
    })();
    return () => { cancelled = true; };
  }, [reportKey, viewAsClientId, siteId, range, from, to]);

  // ─────────  Aggregations (one per report key)  ─────────
  const aggregated = useMemo(() => aggregate(reportKey, rows, capacity), [reportKey, rows, capacity]);

  if (!meta) {
    return (
      <div className="space-y-3" data-testid="report-not-found">
        <Link to="/admin/reports" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> Back to reports
        </Link>
        <h1 className="text-2xl font-semibold">Unknown report</h1>
        <p className="text-sm text-slate-500">No definition for <code>{reportKey}</code>.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`report-${reportKey}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin/reports" className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Reports
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">{meta.title}</h1>
          <p className="text-sm text-slate-500">{meta.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={run} disabled={loading} data-testid="report-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={() => downloadCsv(`${reportKey}.csv`, aggregated.csvRows, aggregated.csvCols)}
            data-testid="report-export-csv"
            disabled={aggregated.csvRows.length === 0}
          >
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-slate-500">Site</label>
            <Select value={siteId || "_all_"} onValueChange={(v) => setSiteId(v === "_all_" ? "" : v)}>
              <SelectTrigger data-testid="report-site-filter"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All sites</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Range</label>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger data-testid="report-range-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {range === "custom" && (
            <>
              <div>
                <label className="text-xs text-slate-500">From</label>
                <AuDateInput value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" />
              </div>
              <div>
                <label className="text-xs text-slate-500">To</label>
                <AuDateInput value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Results — {loading ? "loading…" : `${rows.length} bookings`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aggregated.render({ rows })}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─────────────────  Per-report aggregation  ───────────────── */

function aggregate(key, rows, capacity) {
  switch (key) {
    case "bookings-by-site":      return aggBookingsBySite(rows);
    case "bookings-by-status":    return aggBookingsByStatus(rows);
    case "bookings-by-direction": return aggBookingsByDirection(rows);
    case "driver-utilisation":    return aggDriverUtilisation(rows);
    case "customer-traffic":      return aggCustomerTraffic(rows);
    case "bay-utilisation":       return aggBayUtilisation(rows, capacity);
    case "ncr-summary":           return aggNcrSummary(rows);
    case "pallet-movement":       return aggPalletMovement(rows);
    case "sampling-outcomes":     return aggSamplingOutcomes(rows);
    case "lifecycle-late-missed": return aggLifecycleLateMissed(rows);
    default:                      return { csvRows: rows, csvCols: [], render: () => null };
  }
}

const groupBy = (rows, fn) => {
  const m = new Map();
  for (const r of rows) {
    const k = fn(r);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(r);
  }
  return m;
};

const SimpleTable = ({ headers, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase text-slate-500 border-b">
          {headers.map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={headers.length} className="py-6 text-center text-slate-400 italic">No data</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
            {r.map((c, j) => <td key={j} className="py-2 pr-3">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function aggBookingsBySite(rows) {
  const byKey = groupBy(rows, (r) => r.site?.name || "—");
  const out = [];
  for (const [siteName, group] of byKey) {
    const byStatus = groupBy(group, (r) => r.status);
    out.push({
      site: siteName,
      total: group.length,
      approved: (byStatus.get("approved") || []).length,
      completed: (byStatus.get("completed") || []).length,
      cancelled: (byStatus.get("cancelled") || []).length,
      no_show: (byStatus.get("no_show") || []).length,
    });
  }
  out.sort((a, b) => b.total - a.total);
  return {
    csvRows: out,
    csvCols: [
      { key: "site", label: "Site" },
      { key: "total", label: "Total" },
      { key: "approved", label: "Approved" },
      { key: "completed", label: "Completed" },
      { key: "cancelled", label: "Cancelled" },
      { key: "no_show", label: "No-show" },
    ],
    render: () => <SimpleTable
      headers={["Site","Total","Approved","Completed","Cancelled","No-show"]}
      rows={out.map((r) => [r.site, r.total, r.approved, r.completed, r.cancelled, r.no_show])} />,
  };
}

function aggBookingsByStatus(rows) {
  const byKey = groupBy(rows, (r) => r.status);
  const out = [...byKey.entries()].map(([status, g]) => ({ status, count: g.length }));
  out.sort((a, b) => b.count - a.count);
  return {
    csvRows: out,
    csvCols: [{ key: "status", label: "Status" }, { key: "count", label: "Count" }],
    render: () => (
      <SimpleTable
        headers={["Status", "Count"]}
        rows={out.map((r) => [
          <span key="s" className={`inline-block px-2 py-0.5 rounded text-xs ${statusBadgeClass(r.status)}`}>{r.status}</span>,
          r.count,
        ])}
      />
    ),
  };
}

function aggBookingsByDirection(rows) {
  const inb = rows.filter((r) => r.direction === "inbound").length;
  const out = rows.filter((r) => r.direction === "outbound").length;
  const both = rows.filter((r) => r.direction === "both").length;
  const data = [
    { direction: "inbound", count: inb },
    { direction: "outbound", count: out },
    { direction: "both", count: both },
  ];
  return {
    csvRows: data,
    csvCols: [{ key: "direction", label: "Direction" }, { key: "count", label: "Count" }],
    render: () => <SimpleTable
      headers={["Direction", "Count"]}
      rows={data.map((r) => [r.direction, r.count])} />,
  };
}

function aggDriverUtilisation(rows) {
  const byKey = groupBy(rows.filter((r) => r.assigned_driver_id), (r) => r.assigned_driver_id);
  const out = [];
  for (const [driverId, group] of byKey) {
    const hours = group.reduce((acc, r) => {
      const s = new Date(r.scheduled_start).getTime();
      const e = new Date(r.scheduled_end).getTime();
      return acc + Math.max(0, (e - s) / 36e5);
    }, 0);
    out.push({ driver_id: driverId, count: group.length, hours: Number(hours.toFixed(2)) });
  }
  out.sort((a, b) => b.count - a.count);
  return {
    csvRows: out,
    csvCols: [
      { key: "driver_id", label: "Driver ID" },
      { key: "count", label: "Bookings" },
      { key: "hours", label: "Total hours" },
    ],
    render: () => <SimpleTable
      headers={["Driver ID","Bookings","Total hours"]}
      rows={out.map((r) => [r.driver_id, r.count, r.hours])} />,
  };
}

function aggCustomerTraffic(rows) {
  const byKey = groupBy(rows, (r) => `${r.sender_company || "—"} → ${r.receiver_company || "—"}`);
  const out = [...byKey.entries()].map(([pair, g]) => ({ pair, count: g.length }));
  out.sort((a, b) => b.count - a.count);
  return {
    csvRows: out,
    csvCols: [{ key: "pair", label: "Sender → Receiver" }, { key: "count", label: "Trips" }],
    render: () => <SimpleTable
      headers={["Sender → Receiver","Trips"]}
      rows={out.map((r) => [r.pair, r.count])} />,
  };
}

function aggBayUtilisation(rows, capacity) {
  const live = rows.filter((r) => !DEAD_STATUSES.includes(r.status));
  const byBay = new Map();
  for (const [id, c] of capacity || []) byBay.set(id, { bay: c.bay, site: c.site, open: c.openHours, count: 0, hours: 0 });
  for (const r of live) {
    const k = r.bay_id || r.bay?.id || "—";
    if (!byBay.has(k)) byBay.set(k, { bay: r.bay?.name || "—", site: r.site?.name || "—", open: null, count: 0, hours: 0 });
    const e = byBay.get(k);
    e.count += 1;
    e.hours += Math.max(0, (new Date(r.scheduled_end) - new Date(r.scheduled_start)) / 36e5);
  }
  const siteTotals = {};
  for (const e of byBay.values()) siteTotals[e.site] = (siteTotals[e.site] || 0) + e.count;
  const out = [...byBay.values()].map((e) => ({
    bay: e.bay, site: e.site, count: e.count,
    hours: Number(e.hours.toFixed(2)),
    open_hours: e.open === null ? null : Number(e.open.toFixed(1)),
    utilisation: e.open ? Number(((e.hours / e.open) * 100).toFixed(1)) : null,
    share: siteTotals[e.site] ? Number(((e.count / siteTotals[e.site]) * 100).toFixed(0)) : 0,
  }));
  out.sort((a, b) => a.site.localeCompare(b.site) || (b.utilisation ?? -1) - (a.utilisation ?? -1));
  const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  return {
    csvRows: out,
    csvCols: [
      { key: "site", label: "Site" },
      { key: "bay", label: "Bay" },
      { key: "count", label: "Bookings" },
      { key: "hours", label: "Booked hours" },
      { key: "open_hours", label: "Open hours" },
      { key: "utilisation", label: "Utilisation %" },
      { key: "share", label: "Share of site bookings %" },
    ],
    render: () => (
      <div className="space-y-2" data-testid="bay-utilisation-table">
        {!capacity && <p className="text-xs text-slate-500">Calculating open hours…</p>}
        <SimpleTable
          headers={["Site", "Bay", "Bookings", "Booked hours", "Open hours", "Utilisation", "Share of site"]}
          rows={out.map((r) => [r.site, r.bay, r.count, r.hours, r.open_hours ?? "—", pct(r.utilisation), `${r.share}%`])} />
        <p className="text-[11px] text-slate-400">Open hours = site hours ∩ bay hours on open days, minus daily breaks and blackouts (active bays). Cancelled / rejected / no-show bookings are excluded.</p>
      </div>
    ),
  };
}

function aggNcrSummary() {
  // NCR data comes from lbs_booking_issues — separate query (kept simple here).
  return {
    csvRows: [],
    csvCols: [],
    render: () => (
      <div className="text-sm text-slate-500">
        Use the <Link to="/admin/issues" className="text-blue-600 hover:underline">NCR inbox</Link> for
        full triage. A summary breakdown is built directly in the inbox status chips.
      </div>
    ),
  };
}

function aggPalletMovement(rows) {
  const byKey = groupBy(rows.filter((r) => r.pallet_type), (r) => r.pallet_type);
  const out = [];
  for (const [brand, group] of byKey) {
    const in_qty  = group.reduce((a, r) => a + (r.actual_pallets_in  || r.pallets_in  || 0), 0);
    const out_qty = group.reduce((a, r) => a + (r.actual_pallets_out || r.pallets_out || 0), 0);
    out.push({ brand, in_qty, out_qty, net: in_qty - out_qty });
  }
  out.sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
  return {
    csvRows: out,
    csvCols: [
      { key: "brand", label: "Brand" },
      { key: "in_qty", label: "In" },
      { key: "out_qty", label: "Out" },
      { key: "net", label: "Net" },
    ],
    render: () => <SimpleTable
      headers={["Brand","In","Out","Net"]}
      rows={out.map((r) => [r.brand, r.in_qty, r.out_qty, r.net])} />,
  };
}

function aggSamplingOutcomes() {
  return {
    csvRows: [],
    csvCols: [],
    render: () => (
      <div className="text-sm text-slate-500">
        Use the <Link to="/admin/sampling-queue" className="text-blue-600 hover:underline">Sampling queue</Link>{" "}
        for live counts. A periodic email digest is planned for P2.
      </div>
    ),
  };
}

function aggLifecycleLateMissed(rows) {
  const counts = { late: 0, no_show: 0, cancelled: 0, completed: 0, rejected: 0 };
  for (const r of rows) {
    if (counts[r.status] !== undefined) counts[r.status]++;
  }
  const data = Object.entries(counts).map(([k, v]) => ({ status: k, count: v }));
  return {
    csvRows: data,
    csvCols: [{ key: "status", label: "Lifecycle" }, { key: "count", label: "Count" }],
    render: () => <SimpleTable
      headers={["Lifecycle","Count"]}
      rows={data.map((r) => [r.status, r.count])} />,
  };
}
